// Libs
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { Button, Stack, Typography } from '@mui/material';
// Components, Layouts, Pages
import { BaseLabel } from '~/components/common';
// Others
import { IQuoteDetail } from '~/utils/interface/quotes';
import { formatCurrencyUSD } from '~/utils/helper/common';
import { PaymentOrderStatusEnum } from '~/utils/enum/common';
import { IOrderDetail } from '~/utils/interface/order';
// Styles, images, icons
import styles from './TotalInfoQuoteDetail.module.scss';
import { icons } from '~/assets';

type Props = {
  detailQuote?: IQuoteDetail;
  detailOrder?: IOrderDetail;
  onMakePayment?: () => void;
};

const cx = classNames.bind(styles);

const TotalInfoQuoteDetail = (props: Props) => {
  //#region Destructuring Props
  const { detailQuote, detailOrder, onMakePayment } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/quotes', 'common/base']);
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [data, setData] = useState<IQuoteDetail | IOrderDetail | undefined>(detailQuote || detailOrder);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    setData(detailQuote || detailOrder);
  }, [detailQuote, detailOrder]);
  //#endregion Implement Hook

  //#region Handle Function
  //#endregion Handle Function

  return (
    <Stack id='totalInfoQuoteDetailComponent' gap={3} className={cx('container')}>
      <BaseLabel styleType='title' label={t('quote_detail_title_summary')} icon={icons.commonIconCurrency} />

      <Typography className={cx('subTitle')}>
        {t('form_quotes_title_subtotal', {
          subtotal: formatCurrencyUSD({ value: data?.subtotal, showDecimal: true }),
        })}
      </Typography>

      <Typography className={cx('subTitle')}>
        {t('form_quotes_title_coupon_amount', {
          couponAmount: formatCurrencyUSD({
            value: detailQuote
              ? (data as IQuoteDetail)?.quoteCoupon?.couponAmount
              : (data as IOrderDetail)?.orderCoupon?.couponAmount,
            showDecimal: true,
          }),
        })}
      </Typography>

      <Typography className={cx('subTitle')}>
        {t('form_quotes_title_deposit', {
          deposit: formatCurrencyUSD({ value: data?.depositMadeAmount, showDecimal: true }),
        })}
      </Typography>

      <Typography className={cx('subTitle')}>
        {t('form_quotes_title_tax', {
          taxRate: data?.taxRate || 0,
          taxAmount: formatCurrencyUSD({ value: data?.taxAmount, showDecimal: true }),
        })}
      </Typography>

      <Typography className={cx('subTitle')}>
        {t('form_quotes_title_po_number', { poNumber: data?.poNumber })}
      </Typography>

      <Typography className={cx('textTotal')}>
        {t('form_quotes_title_total', { total: formatCurrencyUSD({ value: data?.total, showDecimal: true }) })}
      </Typography>

      {data?.paymentStatus !== PaymentOrderStatusEnum.PAID && !detailOrder && (
        <Button
          variant='outlined'
          className={cx('buttonMakePayment')}
          onClick={onMakePayment}
          startIcon={<img src={icons.commonIconPayment} alt={t('common_alt_img')} />}
        >
          {t('quote_detail_title_make_payment')}
        </Button>
      )}
    </Stack>
  );
};

export default TotalInfoQuoteDetail;
